import React from "react";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/lib/seo";
import LeadForm from "@/components/common/LeadForm";
import { Breadcrumbs, SectionLabel } from "@/components/common/states";
import { waLink } from "@/lib/constants";
import { track, Events } from "@/lib/analytics";

export default function Contact() {
  const { settings } = useApp();
  const phone = settings?.phone || "9325271272";
  const email = settings?.email || "info@endeavourcastle.com";
  useSEO({ title: "Contact Us", description: "Get in touch with Endeavour Castle for property enquiries across Pune. Call, WhatsApp or send us your requirement.", path: "/contact" });

  return (
    <div className="ec-container py-8 sm:py-12">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
      <div className="mt-4 mb-10">
        <h1 className="font-display text-3xl sm:text-4xl text-foreground">Talk to a Property Advisor</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Have a question about a property or your requirement? Reach out and we'll get back to you.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-12 items-start">
        <div className="rounded-lg border border-border bg-white p-6 sm:p-8">
          <SectionLabel>Send a Message</SectionLabel>
          <LeadForm source="contact" variant="full" submitLabel="Send Message" />
        </div>

        <div className="space-y-4">
          <a href={`tel:${phone}`} onClick={() => track(Events.PHONE, { where: "contact" })} data-testid="contact-call"
            className="flex items-center gap-4 p-5 rounded-md border border-border bg-white hover:border-primary transition-colors">
            <div className="h-11 w-11 rounded-sm bg-primary/5 text-primary grid place-items-center"><Phone className="h-5 w-5" aria-hidden /></div>
            <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Call us</p><p className="font-medium text-foreground">{phone}</p></div>
          </a>
          <a href={waLink(settings?.whatsapp || phone, "Hi, I'd like to enquire about property in Pune.")} target="_blank" rel="noreferrer" onClick={() => track(Events.WHATSAPP, { where: "contact" })} data-testid="contact-whatsapp"
            className="flex items-center gap-4 p-5 rounded-md border border-border bg-white hover:border-primary transition-colors">
            <div className="h-11 w-11 rounded-sm bg-[#25D366]/10 text-[#25D366] grid place-items-center"><MessageCircle className="h-5 w-5" aria-hidden /></div>
            <div><p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</p><p className="font-medium text-foreground">Chat with us</p></div>
          </a>
          <a href={`mailto:${email}`} className="flex items-center gap-4 p-5 rounded-md border border-border bg-white hover:border-primary transition-colors">
            <div className="h-11 w-11 rounded-sm bg-primary/5 text-primary grid place-items-center"><Mail className="h-5 w-5" aria-hidden /></div>
            <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p><p className="font-medium text-foreground">{email}</p></div>
          </a>
          <div className="flex items-center gap-4 p-5 rounded-md border border-border bg-white">
            <div className="h-11 w-11 rounded-sm bg-primary/5 text-primary grid place-items-center"><MapPin className="h-5 w-5" aria-hidden /></div>
            <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Location</p><p className="font-medium text-foreground">{settings?.address || "Pune, Maharashtra, India"}</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}
